import React, { useEffect, useState, useMemo } from 'react';
import './ServicesPage.css';
import { BACKEND_URL } from '../../services/Helper';
import AOS from 'aos';
import 'aos/dist/aos.css';

const PROFESSIONS = [
  { id: 'all',              label: 'All',             icon: '👥' },
  { id: 'pandit',           label: 'Pandit',           icon: '🙏' },
  { id: 'advocate',         label: 'Advocate',         icon: '⚖️' },
  { id: 'ca',               label: 'CA',               icon: '📊' },
  { id: 'teacher',          label: 'Teacher',          icon: '📚' },
  { id: 'doctor',           label: 'Doctor',           icon: '🩺' },
  { id: 'electrician',      label: 'Electrician',      icon: '💡' },
  { id: 'plumber',          label: 'Plumber',          icon: '🔧' },
  { id: 'photographer',     label: 'Photographer',     icon: '📸' },
  { id: 'event organizer',  label: 'Events',           icon: '🎉' },
  { id: 'carpenter',        label: 'Carpenter',        icon: '🪚' },
  { id: 'car wash',         label: 'Car Wash',         icon: '🚗' },
  { id: 'maid',             label: 'Maid',             icon: '🧹' },
  { id: 'cook',             label: 'Cook',             icon: '👨‍🍳' },
  { id: 'society car wash', label: 'Society Car Wash', icon: '🏘️' },
  { id: 'other',            label: 'Other',            icon: '👤' },
];

const PROF_LIST = ['doctor','electrician','plumber','ca','advocate','teacher','pandit','photographer','carpenter','event organizer','car wash','maid','cook','society car wash','other'];

const ServicesPage = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState('all');
  const [search, setSearch] = useState('');
  const [activeProvider, setActiveProvider] = useState(null);
  const [showRegister, setShowRegister] = useState(false);
  const [regLoading, setRegLoading] = useState(false);
  const [regSuccess, setRegSuccess] = useState(false);
  const [regForm, setRegForm] = useState({
    name: '', profession: 'doctor', specialization: '',
    contactNumber: '', email: '', areasServed: '',
    experience: '', feeRange: '', description: '',
  });

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  useEffect(() => {
    setLoading(true);
    fetch(`${BACKEND_URL}/nearbe/getallproviders`)
      .then(r => r.json())
      .then(d => setProviders(d.providers || []))
      .catch(() => setProviders([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return providers.filter(p => {
      const matchProfession = selected === 'all' || p.profession.toLowerCase() === selected;
      const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase())
        || p.specialization?.toLowerCase().includes(search.toLowerCase())
        || p.areasServed?.some(a => a.toLowerCase().includes(search.toLowerCase()));
      return matchProfession && matchSearch;
    });
  }, [providers, selected, search]);

  const profMeta = (profession) =>
    PROFESSIONS.find(p => p.id === profession?.toLowerCase()) || { icon: '👤', label: profession };

  const handleRegChange = (e) => setRegForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleRegSubmit = async (e) => {
    e.preventDefault();
    if (!regForm.name || !regForm.contactNumber || !regForm.email) {
      alert('Name, contact number and email are required.');
      return;
    }
    try {
      setRegLoading(true);
      const res = await fetch(`${BACKEND_URL}/nearbe/registerprovider`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...regForm,
          areasServed: regForm.areasServed.split(',').map(a => a.trim()).filter(Boolean),
          experience: parseInt(regForm.experience, 10) || 0,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Registration failed');
      setRegSuccess(true);
    } catch (err) {
      alert(err.message || 'Registration failed. Try again.');
    } finally {
      setRegLoading(false);
    }
  };

  return (
    <div className="services-page">
      <div className="services-header" data-aos="fade-down">
        <h2 className="services-title">Service Providers</h2>
        <p className="services-sub">Verified professionals in your area — pandits, advocates, CAs and more</p>
        <input
          className="services-search"
          placeholder="Search by name, area, or specialization…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Register CTA */}
      <div style={{ textAlign: 'right', marginBottom: 12 }}>
        <button className="svc-register-fab" onClick={() => { setShowRegister(true); setRegSuccess(false); }}>
          + Register as Provider
        </button>
      </div>

      {/* Profession filter chips */}
      <div className="services-chips">
        {PROFESSIONS.map(p => (
          <button
            key={p.id}
            className={`svc-chip ${selected === p.id ? 'svc-chip--active' : ''}`}
            onClick={() => setSelected(p.id)}
          >
            <span>{p.icon}</span> {p.label}
          </button>
        ))}
      </div>

      {/* Count */}
      {!loading && (
        <p className="services-count" data-aos="fade-in">
          {filtered.length} provider{filtered.length !== 1 ? 's' : ''} found
        </p>
      )}

      {/* Grid */}
      {loading ? (
        <div className="services-loading">
          <div className="svc-spinner" />
          <p>Finding providers…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="services-empty">
          <span>🔍</span>
          <p>No providers found. Try a different filter.</p>
        </div>
      ) : (
        <div className="services-grid">
          {filtered.map((p, i) => {
            const meta = profMeta(p.profession);
            return (
              <div
                key={p._id}
                className="svc-card"
                data-aos="fade-up"
                data-aos-delay={Math.min(i * 60, 300)}
                onClick={() => setActiveProvider(p)}
              >
                <div className="svc-card-top">
                  {p.photo ? (
                    <img className="svc-avatar" src={p.photo} alt={p.name} />
                  ) : (
                    <div className="svc-avatar-emoji">{meta.icon}</div>
                  )}
                  {p.isVerified && (
                    <div className="svc-verified-badge">✓ Nearbeee Verified</div>
                  )}
                </div>
                <div className="svc-card-body">
                  <h3 className="svc-name">{p.name}</h3>
                  <p className="svc-profession">{meta.icon} {p.profession}</p>
                  {p.specialization && (
                    <p className="svc-spec">{p.specialization}</p>
                  )}
                  <div className="svc-meta-row">
                    {p.experience > 0 && (
                      <span className="svc-meta-chip">{p.experience} yr{p.experience !== 1 ? 's' : ''}</span>
                    )}
                    {p.feeRange && (
                      <span className="svc-meta-chip">{p.feeRange}</span>
                    )}
                  </div>
                  {p.areasServed?.length > 0 && (
                    <p className="svc-areas">📍 {p.areasServed.slice(0, 3).join(', ')}{p.areasServed.length > 3 ? '…' : ''}</p>
                  )}
                </div>
                <div className="svc-card-foot">
                  {p.contactNumber && (
                    <a
                      className="svc-call-btn"
                      href={`tel:${p.contactNumber}`}
                      onClick={e => e.stopPropagation()}
                    >
                      📞 Call
                    </a>
                  )}
                  <button className="svc-info-btn" onClick={() => setActiveProvider(p)}>
                    View Profile →
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Provider detail modal */}
      {activeProvider && (
        <ProviderModal provider={activeProvider} profMeta={profMeta} onClose={() => setActiveProvider(null)} />
      )}

      {/* Register as Provider modal */}
      {showRegister && (
        <div className="svc-modal-overlay" onClick={() => setShowRegister(false)}>
          <div className="svc-modal svc-register-modal" onClick={e => e.stopPropagation()}>
            <button className="svc-modal-close" onClick={() => setShowRegister(false)}>✕</button>
            {regSuccess ? (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
                <h3 style={{ color: '#d4af37', marginBottom: 8 }}>Registration Submitted!</h3>
                <p style={{ color: '#a89a78', fontSize: 14 }}>
                  Our team will verify your profile and you'll be listed within 24 hours.
                  Once verified, you can login via the Provider Login page.
                </p>
                <button className="svc-register-fab" style={{ marginTop: 20 }} onClick={() => setShowRegister(false)}>
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleRegSubmit}>
                <h3 style={{ color: '#d4af37', marginBottom: 16 }}>Register as Service Provider</h3>
                <div className="reg-row">
                  <input className="reg-input" name="name" placeholder="Full Name *" value={regForm.name} onChange={handleRegChange} required />
                  <input className="reg-input" name="email" type="email" placeholder="Email *" value={regForm.email} onChange={handleRegChange} required />
                </div>
                <div className="reg-row">
                  <input className="reg-input" name="contactNumber" placeholder="Contact Number *" value={regForm.contactNumber} onChange={handleRegChange} required />
                  <select className="reg-input" name="profession" value={regForm.profession} onChange={handleRegChange}>
                    {PROF_LIST.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                  </select>
                </div>
                <input className="reg-input reg-full" name="specialization" placeholder="Specialization (e.g. Vastu Pandit, Tax CA)" value={regForm.specialization} onChange={handleRegChange} />
                <div className="reg-row">
                  <input className="reg-input" name="experience" type="number" min="0" placeholder="Years of experience (e.g. 5)" value={regForm.experience} onChange={handleRegChange} />
                  <input className="reg-input" name="feeRange" placeholder="Fee Range (e.g. ₹500–₹1000)" value={regForm.feeRange} onChange={handleRegChange} />
                </div>
                <input className="reg-input reg-full" name="areasServed" placeholder="Areas Served (comma separated: Indiranagar, Koramangala)" value={regForm.areasServed} onChange={handleRegChange} />
                <textarea className="reg-input reg-full reg-textarea" name="description" placeholder="Brief description about your services" value={regForm.description} onChange={handleRegChange} rows={3} />
                <button type="submit" className="svc-register-fab reg-submit" disabled={regLoading}>
                  {regLoading ? 'Submitting…' : 'Submit Registration'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

function ProviderModal({ provider: p, profMeta, onClose }) {
  const meta = profMeta(p.profession);
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);
  return (
    <div className="svc-modal-overlay" onClick={onClose}>
      <div className="svc-modal" onClick={e => e.stopPropagation()}>
        <button className="svc-modal-close" onClick={onClose}>✕</button>
        <div className="svc-modal-top">
          {p.photo
            ? <img className="svc-modal-avatar" src={p.photo} alt={p.name} />
            : <div className="svc-modal-avatar-emoji">{meta.icon}</div>}
          <div>
            <h2 className="svc-modal-name">{p.name}</h2>
            <p className="svc-modal-profession">{meta.icon} {p.profession}</p>
            {p.isVerified && <div className="svc-modal-verified">✓ Nearbeee Verified</div>}
          </div>
        </div>
        {p.specialization && <p className="svc-modal-spec">"{p.specialization}"</p>}
        {p.description && <p className="svc-modal-desc">{p.description}</p>}
        <div className="svc-modal-details">
          {p.experience > 0 && <div className="svc-detail-row"><span>⏳ Experience</span><strong>{p.experience} year{p.experience !== 1 ? 's' : ''}</strong></div>}
          {p.feeRange && <div className="svc-detail-row"><span>💰 Fee Range</span><strong>{p.feeRange}</strong></div>}
          {p.areasServed?.length > 0 && <div className="svc-detail-row"><span>📍 Areas Served</span><strong>{p.areasServed.join(', ')}</strong></div>}
          {p.email && <div className="svc-detail-row"><span>✉️ Email</span><strong>{p.email}</strong></div>}
        </div>
        {p.reelLink && (
          <a
            className="svc-modal-reel"
            href={p.reelLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            ▶ Watch Reel
          </a>
        )}
        {p.contactNumber && (
          <a className="svc-modal-call" href={`tel:${p.contactNumber}`}>📞 Call {p.name}</a>
        )}
      </div>
    </div>
  );
}

export default ServicesPage;
